import { Component } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors, ReactiveFormsModule, AsyncValidatorFn } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSelectModule } from '@angular/material/select';
import { AuthService } from '../../core/services/auth.service';
import { Router, RouterLink } from '@angular/router';
import { NotificationService } from '../../shared/services/notification.service';
import { categories } from '../../shared/utils/select-options';
import { TvaService } from '../../shared/services/tva.service';
import { Observable, catchError, first, of, map } from 'rxjs';
import { LogService } from '../../core/services/log.service';

@Component({
  selector: 'app-signup',
  imports: [
    ReactiveFormsModule,
    MatInputModule,
    MatFormFieldModule,
    MatButtonModule,
    MatCheckboxModule,
    MatSelectModule,
    RouterLink
  ],
  templateUrl: './signup.html',
  styleUrl: './signup.scss'
})
export class Signup {
  signupForm: FormGroup;
  isProfessional = false;
  categories = categories.sort();

  constructor(
    private fb: FormBuilder, 
    private authService: AuthService,
    private notificationService: NotificationService,
    private router : Router,
    private tvaService: TvaService,
    private logger: LogService
  ) {
    this.signupForm = this.fb.group(
      {
        firstName: ['', Validators.required],
        lastName: ['', Validators.required],
        email: ['', [Validators.required, Validators.email]],
        password: ['', [Validators.required, Validators.minLength(6)]],
        confirmPassword: ['', Validators.required],
        phoneNumber: ['', Validators.required],
        address: this.fb.group({
          street: ['', Validators.required],
          number: [null, [Validators.required, Validators.min(1)]],
          city: ['', Validators.required],
          zipCode: ['', Validators.required],
          country: ['', Validators.required],
        })
      },
      { validators: this.passwordsMatchValidator }
    );
  }

  passwordsMatchValidator(control: AbstractControl): ValidationErrors | null {
    const password = control.get('password');
    const confirmPassword = control.get('confirmPassword');

    if (password?.value && confirmPassword?.value && password?.value !== confirmPassword?.value) {
      confirmPassword.setErrors({ passwordsMismatch: true });
      return { passwordsMismatch: true };
    } else {
      confirmPassword?.setErrors(null);
      return null;
    }
  }

  tvaAsyncValidator(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      if (!control.value) return of(null);
      return this.tvaService.checkTva(control.value).pipe(
        map(result => {
          this.logger.log(`Résultat vérification TVA pour ${control.value}: ${JSON.stringify(result)}`);
          if (result?.isValid === false) {
            this.logger.warn(`TVA invalide: ${control.value}`, { tva: control.value });
            return { invalidTva: result.message ?? 'TVA invalide' };
          }
          this.logger.log(`TVA valide: ${control.value}`, { tva: control.value });
          return null;
        }),
        catchError(e => {
          this.logger.error(`Erreur lors de la vérification de la TVA: ${JSON.stringify(e)}`, { tva: control.value, error: e });
          return of({ tvaError: 'Impossible de vérifier la TVA' });
        }),
        first()
      );
    };
  }

  toggleProfessional(event: any) {
    this.isProfessional = event.checked;
    if (this.isProfessional) {
      const userAddress = this.signupForm.get('address')?.value;
      
      // ajouter dynamiquement le sous-formulaire company
      this.signupForm.addControl(
        'company',
        this.fb.group({
          companyName: ['', Validators.required],
          companyNumber: ['', Validators.required, [this.tvaAsyncValidator()]],
          roleInCompany: [''],
          companyEmail: ['', [Validators.required, Validators.email]],
          companyPhoneNumber: ['', Validators.required],
          address: this.fb.group({
              street: [userAddress.street, Validators.required],
              number: [userAddress.number, [Validators.required, Validators.min(1)]],
              city: [userAddress.city, Validators.required],
              zipCode: [userAddress.zipCode, Validators.required],
              country: [userAddress.country, Validators.required],
            }),
          category: ['', Validators.required],
          description: [''],
          plan: ['basic', Validators.required]
        })
      );
    } else {
      // retirer le sous-formulaire si décoché
      this.signupForm.removeControl('company');
    }
  }

  selectPlan(plan: 'basic' | 'premium') { 
    this.signupForm.get('company.plan')?.setValue(plan); 
  }

  onSubmit() {
    if (this.signupForm.valid) {
      const formValue = this.signupForm.value;
      const payload = {
        ...formValue,
        userType: this.isProfessional ? 'pro' : 'standard'
      };

      this.authService.register(payload).subscribe({
        next: (res) => {
          this.notificationService.success("Votre compte a été créé avec succès. Un email de confirmation vous a été envoyé afin de valider votre compte.");
          this.router.navigate(["/signin"]);
        },
        error: (err) => {
          this.logger.error('Registration failed: ' + JSON.stringify(err));
          this.notificationService.error("Une erreur est survenu lors de votre inscription");
        }
      });
    }
  }
}
